import * as z from "zod";
import { readStepConfig } from "../commitFiles";
import {
  claimSlug,
  ensureSlug,
  getWorkflowIdForCommit,
} from "../connectorSlug";
import { getConnectorAiContext } from "../connectors/getAiContext";
import { ensureWritable, modifyStepConfig } from "../copyOnWrite";
import { assertSupplierAvailable } from "../rbac/crossResourceAvailability";
import { mutator } from "./mutator";
import { branchSchema, insertUserAction } from "./utils";

export const associateStepWithConnectedApp = mutator({
  schema: z
    .object({
      stepDir: z.string(),
      connectedAppId: z.string(),
      slug: z.string().optional(),
      silent: z.boolean().optional(),
    })
    .merge(branchSchema),
  resolve: {
    app: { kind: "connectedApp", from: "connectedAppId" },
    branch: { kind: "branch", from: "branchId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.branch.workflow.spaceId,
    },
    {
      permission: "connected_app:use",
      scopeId: ({ args }) => args.app.id,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const {
      stepDir,
      slug: claimedSlug,
      silent,
      app,
      branch,
      forkBranchId,
      forkCommitId,
      forkChatId,
      workingCommitId,
      workingChatId,
    } = args;

    await assertSupplierAvailable({
      tx,
      tenantId: ctx.tenantId,
      ruleId: "space_uses_connected_apps",
      dependentScopeId: branch.workflow.spaceId,
      supplierScopeId: app.id,
    });

    const { commitId } = await ensureWritable(
      tx,
      ctx.userId,
      ctx.tenantId,
      {
        branchId: branch.id,
        forkBranchId,
        forkCommitId,
        forkChatId,
        workingCommitId,
        workingChatId,
      },
      branch
    );
    const workflowId = await getWorkflowIdForCommit(tx, commitId);
    const slug = claimedSlug
      ? await claimSlug(tx, workflowId, claimedSlug, app.id, ctx.tenantId)
      : await ensureSlug(
          tx,
          workflowId,
          app.id,
          app.identifier ?? "app",
          app.name ?? app.id,
          ctx.tenantId
        );

    const identifier = app.identifier ?? "app";
    const config = await readStepConfig({ tx, commitId }, stepDir);
    const apps = config.apps ?? [];
    if (!apps.some((a) => a.name === slug)) {
      apps.push({ name: slug, type: identifier });
    }

    await modifyStepConfig(tx, commitId, ctx.tenantId, stepDir, { apps });

    if (!silent) {
      const aiContext = getConnectorAiContext(app);
      await insertUserAction(
        tx,
        commitId,
        ctx.userId,
        ctx.tenantId,
        "associateStepWithConnectedApp",
        {
          stepDir,
          connectedAppId: app.id,
          connectedAppName: app.name ?? app.id,
          ...(aiContext && { aiContext }),
        }
      );
    }
  },
});
